import { useState, useEffect, useCallback } from 'react';

/**
 * Fetches user info from Azure Static Web Apps authentication.
 *
 * In production (NODE_ENV !== 'development') this hits `/.auth/me`.
 * In development the returned `userDetails` is resolved with this
 * precedence:
 *   1. `?as=<email>` query string on the current URL — top precedence so
 *      you can swap identities mid-session without a restart. Useful for
 *      testing admin vs. non-admin flows.
 *   2. `REACT_APP_DEV_USER` environment variable — typically set per
 *      developer in `.env.local` (gitignored).
 *   3. Fallback literal `'local'` — preserves the original behaviour.
 *
 * @returns {Promise<Object|undefined>} User info object or undefined if not authenticated
 */
export const getUserInfo = async () => {
  try {
    if (process.env.NODE_ENV === "development") {
      let devUser = process.env.REACT_APP_DEV_USER;
      if (typeof window !== 'undefined' && window.location?.search) {
        const asParam = new URLSearchParams(window.location.search).get('as');
        if (asParam) devUser = asParam;
      }
      return {
        userDetails: devUser || 'local',
        userRoles: ['authenticated'],
      };
    }
    const response = await fetch('/.auth/me');
    const payload = await response.json();
    const { clientPrincipal } = payload;
    return clientPrincipal;
  } catch (error) {
    console.error('No profile could be found', error);
    return undefined;
  }
};

/**
 * Reduce a window.location-like object to a safe same-origin relative path
 * for use as a post-login/logout redirect target.
 *
 * Using the absolute `window.location.href` (the previous behaviour) makes
 * `post_login_redirect_uri` an open redirect: anything that can influence
 * the current URL can bounce the user to an attacker-controlled site after
 * a legitimate AAD login. A leading-single-slash relative path can only
 * land back on this origin. Protocol-relative (`//evil.com`) and
 * backslash-escape (`/\evil.com`) forms are rejected to `/`.
 *
 * @param {{pathname?: string, search?: string, hash?: string}} loc
 * @returns {string} same-origin relative path, `/` if unsafe or missing
 */
export const sanitizeRedirectPath = (loc) => {
  if (!loc) return '/';
  const path = `${loc.pathname || '/'}${loc.search || ''}${loc.hash || ''}`;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
    return '/';
  }
  return path;
};

/**
 * Hook for managing Azure Static Web Apps authentication state
 *
 * @returns {{
 *   userInfo: Object|undefined,
 *   isAuthenticated: boolean,
 *   isLoading: boolean,
 *   loginUrl: string,
 *   logoutUrl: string,
 *   refreshAuth: () => Promise<void>
 * }}
 *
 * @example
 * const { userInfo, isAuthenticated, loginUrl, logoutUrl } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <Button href={loginUrl}>Log In</Button>;
 * }
 */
export const useAuth = () => {
  const [userInfo, setUserInfo] = useState(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const redirect = encodeURIComponent(
    typeof window !== 'undefined' ? sanitizeRedirectPath(window.location) : '/'
  );

  const refreshAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const info = await getUserInfo();
      setUserInfo(info);
      setIsAuthenticated(
        info?.userRoles?.includes('authenticated') ?? false
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  return {
    userInfo,
    isAuthenticated,
    isLoading,
    loginUrl: `/.auth/login/aad?post_login_redirect_uri=${redirect}`,
    logoutUrl: `/.auth/logout?post_logout_redirect_uri=${redirect}`,
    refreshAuth,
  };
};

export default useAuth;
